import { gql, useLazyQuery } from "@apollo/client";
import {
  Card,
  Divider,
  Drawer,
  Grid,
  Icon,
  MenuItem,
  Select,
  Tab,
  Tabs,
  Tooltip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import Loading from "components/Carteles/Loading";
import SinDatos from "components/Carteles/SinDatos";
import CustomPagination from "components/Pagination";
import SoftBadge from "components/SoftBadge";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";
import { MenuContext } from "context/menuContext";
import dayjs from "dayjs";
import PropTypes from "prop-types";
import { useContext, useEffect, useRef, useState } from "react";
import { getUproColor } from "utils/colors";
import {
  formatDate,
  formatStockQuantity,
  formatUnitQuantity,
} from "utils/formatters";

const tipos = ["Material", "Consumible", "Recurso", "Plato", "Plato_Ingrediente"];
const icons = ["category", "recycling", "layers", "restaurant", "inventory_2"];
const colors = ["uproYellow", "uproGreen", "uproPink", "uproBlue", "uproBlue"];

const DetailDrawer = styled(Drawer)(({ theme }) => ({
  "& .MuiDrawer-paper": {
    width: "100%",
    maxWidth: 1120,
    border: "none",
    boxShadow: theme.boxShadows?.xxl || theme.shadows[8],
    backgroundColor: theme.palette.background.default,
  },
}));

const SummaryCard = styled(Card)(({ theme }) => ({
  height: "100%",
  boxShadow: "none",
  border: `1px solid ${theme.palette.grey[200]}`,
  borderRadius: theme.borderRadius?.xl || "1rem",
  display: "flex",
}));

const MovementTableContainer = styled(SoftBox)(({ theme }) => ({
  overflowX: "auto",
  border: `1px solid ${theme.palette.grey[200]}`,
  borderRadius: theme.borderRadius?.xl || "1rem",
}));

const MovementTable = styled("table")(({ theme }) => ({
  width: "100%",
  minWidth: 920,
  borderCollapse: "collapse",
  tableLayout: "fixed",
  "& thead th": {
    padding: "0.9rem 1rem",
    textAlign: "left",
    fontSize: "0.72rem",
    fontWeight: 700,
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
    borderBottom: `1px solid ${theme.palette.grey[200]}`,
    backgroundColor: theme.palette.background.paper,
  },
  "& tbody td": {
    padding: "0.9rem 1rem",
    verticalAlign: "top",
    borderBottom: `1px solid ${theme.palette.grey[200]}`,
  },
  "& tbody tr:hover": {
    backgroundColor: theme.palette.grey[100],
  },
  "& tbody tr:last-of-type td": {
    borderBottom: "none",
  },
  "& th:first-of-type, & td:first-of-type": {
    textAlign: "left",
  },
  "& th:last-of-type, & td:last-of-type": {
    textAlign: "right",
  },
  "& th:not(:first-of-type):not(:last-of-type), & td:not(:first-of-type):not(:last-of-type)": {
    textAlign: "center",
  },
}));

const MovementStage = styled(SoftBox)({
  position: "relative",
  width: "100%",
  overflow: "hidden",
  minHeight: 30,
});

const MovementPanel = styled(SoftBox, {
  shouldForwardProp: (prop) => prop !== "active" && prop !== "hiddenDirection",
})(({ active, hiddenDirection }) => ({
  width: "100%",
  transition: "transform 280ms ease, opacity 280ms ease",
  transform: active ? "translateX(0)" : hiddenDirection === "left" ? "translateX(-100%)" : "translateX(100%)",
  opacity: active ? 1 : 0,
  pointerEvents: active ? "auto" : "none",
  position: active ? "relative" : "absolute",
  inset: 0,
}));

const buildPresetMovimientoFilters = (option = "hoy") => {
  switch (option) {
    case "ayer":
      return {
        desde: dayjs().subtract(1, "day").format("YYYY-MM-DD"),
        hasta: dayjs().subtract(1, "day").format("YYYY-MM-DD"),
        tipo: "TODOS",
      };
    case "Ultimos 7 días":
      return {
        desde: dayjs().subtract(7, "day").format("YYYY-MM-DD"),
        hasta: dayjs().format("YYYY-MM-DD"),
        tipo: "TODOS",
      };
    case "Ultimos 30 días":
      return {
        desde: dayjs().subtract(30, "day").format("YYYY-MM-DD"),
        hasta: dayjs().format("YYYY-MM-DD"),
        tipo: "TODOS",
      };
    case "personalizado":
      return {
        desde: dayjs().format("YYYY-MM-DD"),
        hasta: dayjs().format("YYYY-MM-DD"),
        tipo: "TODOS",
      };
    case "hoy":
    default:
      return {
        desde: dayjs().format("YYYY-MM-DD"),
        hasta: dayjs().format("YYYY-MM-DD"),
        tipo: "TODOS",
      };
  }
};

const getMovimientosFilterDefaults = () => ({
  ...buildPresetMovimientoFilters("hoy"),
});

const formatSignedStock = (cantidad, unidad) => {
  const value = Number(cantidad || 0);
  if (value === 0) return formatStockQuantity(0, unidad);
  return `${value > 0 ? "+" : "-"}${formatStockQuantity(Math.abs(value), unidad)}`;
};

const getCantidadTone = (cantidad) => {
  const value = Number(cantidad || 0);
  if (value > 0) return "success";
  if (value < 0) return "error";
  return "secondary";
};

const formatDetailDateValue = (value) => {
  if (!value) return "-";
  return formatDate(value, "dd/MM/yyyy hh:mm") || "-";
};

const normalizeDetailRowLabel = (label) =>
  String(label || "")
    .trim()
    .toLowerCase();

const getDetailRowValue = (detalle, targetLabel) => {
  const target = normalizeDetailRowLabel(targetLabel);
  const row = (detalle?.rows || []).find(
    (item) => normalizeDetailRowLabel(item?.label) === target
  );
  return row?.value || null;
};

const isRemitoProveedorMovimiento = (movimiento) => {
  const descripcion = String(movimiento?.descripcion || "").trim();
  const titulo = String(movimiento?.detalle?.titulo || "").trim();
  return descripcion === "Remito Proveedor" || titulo.startsWith("Remito Proveedor");
};

const ActionButton = ({ icon, color, label, onClick, disabled = false }) => (
  <SoftButton
    variant="outlined"
    color={color}
    onClick={onClick}
    disabled={disabled}
    sx={{ justifyContent: "flex-start" }}
    fullWidth
  >
    <Icon sx={{ fontWeight: "light" }}>{icon}</Icon>
    &nbsp;{label}
  </SoftButton>
);

function InfoRow({ label, value, tone = "dark" }) {
  return (
    <SoftBox display="flex" justifyContent="space-between" gap={2}>
      <SoftTypography variant="caption" color="text">
        {label}
      </SoftTypography>
      <SoftTypography variant="caption" fontWeight="bold" color={tone} textAlign="right">
        {value}
      </SoftTypography>
    </SoftBox>
  );
}

function SectionCard({ title, subtitle = null, children, action = null }) {
  return (
    <Card>
      <SoftBox p={2.5}>
        <SoftBox
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          gap={2}
          mb={children ? 2 : 0}
        >
          <SoftBox>
            <SoftTypography variant="h6">{title}</SoftTypography>
            {subtitle ? (
              <SoftTypography variant="caption" color="text">
                {subtitle}
              </SoftTypography>
            ) : null}
          </SoftBox>
          {action}
        </SoftBox>
        {children}
      </SoftBox>
    </Card>
  );
}

function SummaryStatCard({ icon, iconColor, label, value, hint, hintColor = "text" }) {
  return (
    <SummaryCard>
      <SoftBox p={2} display="flex" flexDirection="column" width="100%">
        <SoftBox
          width="2.9rem"
          height="2.9rem"
          borderRadius="lg"
          display="flex"
          justifyContent="center"
          alignItems="center"
          shadow="md"
          sx={{ backgroundColor: getUproColor(iconColor) }}
        >
          <Icon sx={{ color: "#fff", fontSize: "1.2rem" }}>{icon}</Icon>
        </SoftBox>
        <SoftBox mt={1.5}>
          <SoftTypography
            variant="button"
            fontWeight="bold"
            color="dark"
            display="block"
            sx={{ whiteSpace: "normal", overflowWrap: "anywhere" }}
          >
            {label}
          </SoftTypography>
          <SoftTypography
            variant="h4"
            fontWeight="bold"
            color="dark"
            lineHeight={1.1}
            mt={0.5}
            display="block"
            sx={{ whiteSpace: "normal", overflowWrap: "anywhere" }}
          >
            {value}
          </SoftTypography>
          <SoftTypography
            variant="caption"
            color={hintColor}
            fontWeight="medium"
            display="block"
            mt={0.75}
            sx={{
              whiteSpace: "normal",
              overflowWrap: "anywhere",
              wordBreak: "break-word",
              lineHeight: 1.35,
            }}
          >
            {hint}
          </SoftTypography>
        </SoftBox>
      </SoftBox>
    </SummaryCard>
  );
}

export default function DrawerProductoDetalle({
  open,
  handleClose: onClose,
  item,
  idSucursal,
  idSede,
  onEdit,
  onAjuste,
  onMerma,
  onTransferencia,
  onTransferHistory,
  onIngresar,
  onToggleEnabled,
  onPublicar,
}) {
  const { menu } = useContext(MenuContext);
  const drawerRef = useRef(null);
  const handleClose = (...args) => {
    const focused = document.activeElement;
    // Retirar el foco antes de que MUI marque el panel saliente aria-hidden.
    // El FocusTrap conserva su restauración al control que abrió el panel.
    if (drawerRef.current?.contains(focused)) focused.blur();
    onClose?.(...args);
  };
  const [tabValue, setTabValue] = useState(0);
  const [producto, setProducto] = useState(null);
  const [movimientosPage, setMovimientosPage] = useState(1);
  const [movimientosFilters, setMovimientosFilters] = useState(() => getMovimientosFilterDefaults());
  const [selectedMovimientoHistorial, setSelectedMovimientoHistorial] = useState(null);
  const [expandActive, setExpandActive] = useState(false);
  const [fechaOption, setFechaOption] = useState("hoy");

  const [getProducto, { loading }] = useLazyQuery(
    gql`
      query getProductoDetalle(
        $idProducto: ID
        $idSucursal: ID
        $movimientosOptions: MateriaPrimaMovimientosHistorialInput
      ) {
        materiasPrimas(filter: { id: $idProducto }) {
          id
          codigo
          tipo
          nombre
          disabled
          categoria
          descripcion
          precio
          motivoBaja
          porcentajeUtilizable
          pendienteRecepcionar
          nombreCompra
          tipoUnidad {
            id
            nombre
            abreviatura
            multiplicador
          }
          stock(idSucursal: $idSucursal) {
            cantidad
            enPrestamo
            reservado
            disponible
            reservasPorPedido {
              idPedido
              cantidadReservada
            }
            pasillo
            fila
            columna
            stockMinimo
          }
          movimientosHistorial(idSucursal: $idSucursal, options: $movimientosOptions) {
            stockInicial
            pagination {
              total
              pages
            }
            items {
              id
              fecha
              stockInicial
              descripcion
              usuario
              cantidad
              stockPrevio
              stockAcumulado
              detalle
            }
          }
        }
      }
    `,
    { fetchPolicy: "network-only" }
  );

  useEffect(() => {
    if (open && item?.id && idSucursal) {
      getProducto({
        variables: {
          idProducto: item.id,
          idSucursal,
          movimientosOptions: {
            page: movimientosPage,
            limit: 10,
            tipo: movimientosFilters.tipo,
            desde: movimientosFilters.desde || null,
            hasta: movimientosFilters.hasta || null,
          },
        },
      }).then(({ data }) => {
        setProducto(data?.materiasPrimas?.[0] || null);
      });
    }
  }, [open, item?.id, idSucursal, idSede, movimientosPage, movimientosFilters]);

  useEffect(() => {
    if (!open) {
      setProducto(null);
      setTabValue(0);
      setMovimientosPage(1);
      setMovimientosFilters(getMovimientosFilterDefaults());
      setSelectedMovimientoHistorial(null);
      setExpandActive(false);
      setFechaOption("hoy");
    }
  }, [open]);

  const detalle = producto || item;
  const stockActual = Number(detalle?.stock?.cantidad || 0);
  const stockPrestamo = Number(detalle?.stock?.enPrestamo || 0);
  const stockReservado = Number(detalle?.stock?.reservado || 0);
  const stockDisponible = Number(detalle?.stock?.disponible ?? stockActual - stockReservado);
  const reservasPorPedido = detalle?.stock?.reservasPorPedido || [];
  const pendienteRecepcionar = Number(detalle?.pendienteRecepcionar || 0);
  const porcentajeUtilizable = Number(detalle?.porcentajeUtilizable || 0);
  const stockUtilizable = Math.trunc((stockDisponible * porcentajeUtilizable) / 100);
  const stockDesechos = Math.max(stockDisponible - stockUtilizable, 0);
  const movimientosHistorial = detalle?.movimientosHistorial || {
    stockInicial: 0,
    pagination: { total: 0, pages: 0 },
    items: [],
  };
  const selectedMovimientoEsRemitoProveedor =
    isRemitoProveedorMovimiento(selectedMovimientoHistorial);
  const selectedMovimientoTipo = String(
    getDetailRowValue(selectedMovimientoHistorial?.detalle, "Tipo") ||
    selectedMovimientoHistorial?.descripcion ||
    ""
  )
    .trim()
    .toLowerCase();
  const selectedMovimientoEsVenta = selectedMovimientoTipo === "venta";
  const selectedMovimientoProvision = selectedMovimientoEsRemitoProveedor
    ? getDetailRowValue(selectedMovimientoHistorial?.detalle, "Orden de provision")
    : null;
  const selectedMovimientoDetailRows = (selectedMovimientoHistorial?.detalle?.rows || []).filter(
    (row) =>
      ![
        selectedMovimientoEsRemitoProveedor &&
        normalizeDetailRowLabel(row?.label) === "orden de provision",
        selectedMovimientoEsVenta && normalizeDetailRowLabel(row?.label) === "observaciones",
      ].some(Boolean)
  );

  const tipoIndex = tipos.indexOf(detalle?.tipo);
  const canPublish = !detalle?.disabled && stockActual > 0;
  const ubicacionActual = [detalle?.stock?.pasillo, detalle?.stock?.fila, detalle?.stock?.columna]
    .filter(Boolean)
    .join(" / ");
  const actionButtons = [
    onEdit ? { key: "edit", icon: "edit", color: "primary", label: "Editar", onClick: onEdit } : null,
    onTransferHistory
      ? {
        key: "transfer-history",
        icon: "history",
        color: "info",
        label: "Historial de transferencias",
        onClick: onTransferHistory,
      }
      : null,
    onToggleEnabled
      ? {
        key: "toggle-enabled",
        icon: detalle?.disabled ? "arrow_upward" : "arrow_downward",
        color: detalle?.disabled ? "success" : "error",
        label: detalle?.disabled ? "Habilitar" : "Deshabilitar",
        onClick: onToggleEnabled,
      }
      : null,
    onPublicar
      ? {
        key: "publish",
        icon: "sell",
        color: "dark",
        label: "Publicar como producto",
        onClick: onPublicar,
        disabled: !canPublish,
      }
      : null,
  ].filter(Boolean);

  useEffect(() => {
    if (!open || !item?.id) return;
    setMovimientosPage(1);
    setMovimientosFilters(getMovimientosFilterDefaults());
    setSelectedMovimientoHistorial(null);
    setExpandActive(false);
    setFechaOption("hoy");
    setTabValue(0);
  }, [open, item?.id]);

  useEffect(() => {
    const currentItems = movimientosHistorial?.items || [];
    if (!currentItems.length) {
      setSelectedMovimientoHistorial(null);
      return;
    }

    if (!selectedMovimientoHistorial) return;

    const found = currentItems.find((movement) => movement.id === selectedMovimientoHistorial.id);
    if (!found) {
      setSelectedMovimientoHistorial(null);
    } else if (found !== selectedMovimientoHistorial) {
      setSelectedMovimientoHistorial(found);
    }
  }, [movimientosHistorial?.items]);

  useEffect(() => {
    if (!actionButtons.length && tabValue === 2) {
      setTabValue(0);
    }
  }, [actionButtons.length, tabValue]);

  const handleMovimientoFilterChange = (field, value) => {
    setMovimientosPage(1);
    setSelectedMovimientoHistorial(null);
    setMovimientosFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSelectFechaOption = (nextFechaOption) => {
    setMovimientosPage(1);
    setSelectedMovimientoHistorial(null);
    setFechaOption(nextFechaOption);
    if (nextFechaOption !== "personalizado") {
      setMovimientosFilters((prev) => ({
        ...prev,
        ...buildPresetMovimientoFilters(nextFechaOption),
      }));
    }
    setExpandActive(false);
  };

  return (
    <DetailDrawer ref={drawerRef} inert={open ? undefined : ""} anchor="right" open={open} onClose={handleClose}>
      <SoftBox display="flex" flexDirection="column" height="100%">
        <SoftBox p={3} pb={2}>
          <SoftBox display="flex" justifyContent="space-between" alignItems="flex-start" gap={2}>
            <SoftBox display="flex" gap={1.5} alignItems="flex-start">
              <SoftBox
                width="3rem"
                height="3rem"
                borderRadius="md"
                display="flex"
                justifyContent="center"
                alignItems="center"
                shadow="md"
                sx={{ backgroundColor: getUproColor(colors[tipoIndex] || "uproGreen") }}
              >
                <Icon sx={{ color: "#fff" }}>{icons[tipoIndex] || "layers"}</Icon>
              </SoftBox>
              <SoftBox>
                <SoftTypography variant="h5">{detalle?.nombre || "Detalle"}</SoftTypography>
                <SoftTypography variant="button" color="text">
                  {`ID ${detalle?.id || "-"}${detalle?.codigo ? ` - ${detalle.codigo}` : ""}`}
                </SoftTypography>
                <SoftBox mt={0.75} display="flex" flexWrap="wrap" gap={1}>
                  {detalle?.categoria ? (
                    <SoftBadge color="secondary" size="xs" badgeContent={detalle.categoria} container />
                  ) : null}
                  <SoftBadge
                    color={detalle?.disabled ? "error" : "success"}
                    size="xs"
                    badgeContent={detalle?.disabled ? "Deshabilitado" : "Activo"}
                    container
                  />
                  {stockReservado > 0 ? (
                    <SoftBadge
                      color="info"
                      size="xs"
                      badgeContent={`Reservado ${formatStockQuantity(
                        stockReservado,
                        detalle?.tipoUnidad
                      )}`}
                      container
                    />
                  ) : null}
                </SoftBox>
              </SoftBox>
            </SoftBox>
            <Tooltip title="Cerrar" placement="left">
              <SoftButton color="secondary" circular iconOnly onClick={handleClose}>
                <Icon>close</Icon>
              </SoftButton>
            </Tooltip>
          </SoftBox>
        </SoftBox>

        <SoftBox px={3} pb={2}>
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={4}>
              <SummaryStatCard
                icon="inventory_2"
                iconColor="uproPink"
                label="Fisico"
                value={formatStockQuantity(stockActual, detalle?.tipoUnidad)}
                hint="Stock cargado"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <SummaryStatCard
                icon="lock_clock"
                iconColor="uproBlue"
                label="Reservado"
                value={formatStockQuantity(stockReservado, detalle?.tipoUnidad)}
                hint="Comprometido por reservas"
                hintColor="info"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <SummaryStatCard
                icon="check_circle"
                iconColor="uproGreen"
                label="Disponible"
                value={formatStockQuantity(stockDisponible, detalle?.tipoUnidad)}
                hint="Disponible real"
                hintColor="success"
              />
            </Grid>
          </Grid>
        </SoftBox>

        <SoftBox px={3} pb={2}>
          <Card>
            <Tabs
              value={tabValue}
              onChange={(_, nextValue) => setTabValue(nextValue)}
              variant="fullWidth"
              sx={{
                minHeight: 0,
                "& .MuiTabs-indicator": {
                  backgroundColor: getUproColor("uproGreen"),
                  height: 3,
                },
                "& .MuiTab-root": {
                  minHeight: 48,
                  fontSize: "0.75rem",
                  fontWeight: 700,
                },
              }}
            >
              <Tab label="Resumen" />
              <Tab label="Movimientos" />
              {actionButtons.length ? <Tab label="Acciones" /> : null}
            </Tabs>
          </Card>
        </SoftBox>

        <SoftBox px={3} pb={3} sx={{ overflowY: "auto", flex: 1 }}>
          {loading && !producto ? (
            <Loading />
          ) : (
            <>
              {tabValue === 0 && (
                <SoftBox display="flex" flexDirection="column" gap={2}>
                  <SectionCard title="Estado de stock" subtitle="Resumen operativo de la sucursal actual">
                    <SoftBox display="flex" flexDirection="column" gap={1}>
                      <InfoRow
                        label="Stock fisico"
                        value={formatStockQuantity(stockActual, detalle?.tipoUnidad)}
                      />
                      <InfoRow
                        label="Stock reservado"
                        value={formatStockQuantity(stockReservado, detalle?.tipoUnidad)}
                        tone="info"
                      />
                      {stockReservado > 0 && reservasPorPedido.length ? (
                        <SoftBox ml={1.5} mt={-0.25} display="flex" flexDirection="column" gap={0.5}>
                          {reservasPorPedido.map((reserva) => (
                            <SoftBox
                              key={reserva.idPedido}
                              display="flex"
                              justifyContent="space-between"
                              gap={2}
                            >
                              <SoftTypography variant="caption" color="text">
                                {`Pedido #${reserva.idPedido}`}
                              </SoftTypography>
                              <SoftTypography variant="caption" fontWeight="bold" color="info">
                                {formatStockQuantity(reserva.cantidadReservada, detalle?.tipoUnidad)}
                              </SoftTypography>
                            </SoftBox>
                          ))}
                        </SoftBox>
                      ) : null}
                      <InfoRow
                        label="Disponible real"
                        value={formatStockQuantity(stockDisponible, detalle?.tipoUnidad)}
                        tone="success"
                      />
                      <InfoRow
                        label="A recibir"
                        value={formatUnitQuantity(pendienteRecepcionar, detalle?.tipoUnidad)}
                      />
                    </SoftBox>
                  </SectionCard>

                  <SectionCard title="Detalle del item" subtitle={detalle?.tipoUnidad?.nombre || "-"}>
                    <SoftBox display="flex" flexDirection="column" gap={1}>
                      <InfoRow
                        label="Utilizable"
                        value={formatStockQuantity(stockUtilizable, detalle?.tipoUnidad)}
                      />
                      <InfoRow
                        label="Desechos"
                        value={formatStockQuantity(stockDesechos, detalle?.tipoUnidad)}
                      />
                      <InfoRow label="Porcentaje utilizable" value={`${porcentajeUtilizable}%`} />
                      <InfoRow label="Stock minimo" value={detalle?.stock?.stockMinimo ?? "-"} />
                      <InfoRow label="Ubicacion" value={ubicacionActual || "-"} />
                      {detalle?.nombreCompra ? (
                        <InfoRow label="Nombre de compra" value={detalle.nombreCompra} />
                      ) : null}
                    </SoftBox>
                  </SectionCard>

                  {detalle?.motivoBaja ? (
                    <SectionCard title="Motivo de baja">
                      <SoftTypography variant="button">{detalle.motivoBaja}</SoftTypography>
                    </SectionCard>
                  ) : null}

                  {detalle?.descripcion ? (
                    <SectionCard title="Descripcion">
                      <SoftTypography variant="button">{detalle.descripcion}</SoftTypography>
                    </SectionCard>
                  ) : null}
                </SoftBox>
              )}

              {tabValue === 1 && (
                <SoftBox display="flex" flexDirection="column" gap={1.5}>
                  <SectionCard
                    title="Movimientos confirmados"
                    subtitle="Historial con stock inicial del rango y acumulado segun el filtro visible"
                  >
                    <Grid container spacing={1.5}>
                      <Grid item xs={12} md={fechaOption === "personalizado" ? 4 : 6}>
                        <SoftTypography variant="caption" color="text" fontWeight="bold">
                          Periodo
                        </SoftTypography>
                        <SoftBox mt={0.5}>
                          <SoftButton
                            color="primary"
                            circular
                            onClick={(event) => {
                              setExpandActive(true);
                              menu({
                                open: event.currentTarget,
                                align: "right",
                                options: [
                                  {
                                    name: "Hoy",
                                    icon: {
                                      icon: "calendar_today",
                                      color: getUproColor(fechaOption === "hoy" ? "uproGreen" : "uproBlue"),
                                    },
                                    onClick: () => handleSelectFechaOption("hoy"),
                                  },
                                  {
                                    name: "Ayer",
                                    icon: {
                                      icon: "event",
                                      color: getUproColor(fechaOption === "ayer" ? "uproGreen" : "uproBlue"),
                                    },
                                    onClick: () => handleSelectFechaOption("ayer"),
                                  },
                                  {
                                    name: "Ultimos 7 días",
                                    icon: {
                                      icon: "date_range",
                                      color: getUproColor(
                                        fechaOption === "Ultimos 7 días" ? "uproGreen" : "uproBlue"
                                      ),
                                    },
                                    onClick: () => handleSelectFechaOption("Ultimos 7 días"),
                                  },
                                  {
                                    name: "Ultimos 30 días",
                                    icon: {
                                      icon: "calendar_month",
                                      color: getUproColor(
                                        fechaOption === "Ultimos 30 días" ? "uproGreen" : "uproBlue"
                                      ),
                                    },
                                    onClick: () => handleSelectFechaOption("Ultimos 30 días"),
                                  },
                                  {
                                    name: "Personalizado",
                                    icon: {
                                      icon: "edit_calendar",
                                      color: getUproColor(
                                        fechaOption === "personalizado" ? "uproGreen" : "uproBlue"
                                      ),
                                    },
                                    onClick: () => handleSelectFechaOption("personalizado"),
                                  },
                                ],
                                handleClose: () => { },
                                onClose: () => setExpandActive(false),
                              });
                            }}
                          >
                            {fechaOption}&nbsp;
                            <Icon
                              sx={{
                                rotate: expandActive ? "180deg" : "0deg",
                                transition: "all 0.5s",
                              }}
                            >
                              expand_more
                            </Icon>
                          </SoftButton>
                        </SoftBox>
                      </Grid>
                      {fechaOption === "personalizado" ? (
                        <>
                          <Grid item xs={12} md={4}>
                            <SoftTypography variant="caption" color="text" fontWeight="bold">
                              Desde
                            </SoftTypography>
                            <SoftBox mt={0.5}>
                              <SoftInput
                                type="date"
                                value={movimientosFilters.desde || ""}
                                onChange={(event) =>
                                  handleMovimientoFilterChange("desde", event.target.value)
                                }
                              />
                            </SoftBox>
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <SoftTypography variant="caption" color="text" fontWeight="bold">
                              Hasta
                            </SoftTypography>
                            <SoftBox mt={0.5}>
                              <SoftInput
                                type="date"
                                value={movimientosFilters.hasta || ""}
                                onChange={(event) =>
                                  handleMovimientoFilterChange("hasta", event.target.value)
                                }
                              />
                            </SoftBox>
                          </Grid>
                        </>
                      ) : null}
                      <Grid item xs={12} md={4}>
                        <SoftTypography variant="caption" color="text" fontWeight="bold">
                          Tipo
                        </SoftTypography>
                        <SoftBox mt={0.5}>
                          <Select
                            fullWidth
                            value={movimientosFilters.tipo}
                            onChange={(event) =>
                              handleMovimientoFilterChange("tipo", event.target.value)
                            }
                            sx={{ minHeight: 43 }}
                          >
                            <MenuItem value="TODOS">Todos</MenuItem>
                            <MenuItem value="INGRESO">Ingreso</MenuItem>
                            <MenuItem value="EGRESO">Egreso</MenuItem>
                          </Select>
                        </SoftBox>
                      </Grid>
                    </Grid>
                  </SectionCard>

                  {(movimientosHistorial?.items || []).length === 0 ? (
                    <SinDatos />
                  ) : (
                    <MovementStage>
                      <MovementPanel active={!selectedMovimientoHistorial} hiddenDirection="left">
                        <Card>
                          <SoftBox p={2.5} pb={0}>
                            <SoftBox
                              display="flex"
                              justifyContent="space-between"
                              alignItems={{ xs: "flex-start", sm: "center" }}
                              flexDirection={{ xs: "column", sm: "row" }}
                              gap={1}
                            >
                              <SoftBox>
                                <SoftTypography variant="h6">Tabla de movimientos</SoftTypography>
                              </SoftBox>
                              <SoftBadge
                                color="secondary"
                                size="xs"
                                badgeContent={`${movimientosHistorial?.pagination?.total || 0} registros`}
                                container
                              />
                            </SoftBox>
                          </SoftBox>

                          <SoftBox p={2.5}>
                            <MovementTableContainer>
                              <MovementTable>
                                <thead>
                                  <tr>
                                    <th>Fecha</th>
                                    <th>Stock al inicio</th>
                                    <th>Movimiento</th>
                                    <th>Cantidad</th>
                                    <th>Stock acumulado</th>
                                    <th />
                                  </tr>
                                </thead>
                                <tbody>
                                  {(movimientosHistorial?.items || []).map((movimiento) => (
                                    <tr
                                      key={movimiento.id}
                                      onClick={() => setSelectedMovimientoHistorial(movimiento)}
                                      style={{ cursor: "pointer" }}
                                    >
                                      <td>
                                        <SoftTypography variant="caption" color="dark" fontWeight="medium">
                                          {formatDate(movimiento.fecha, "XXXX a las hh:mm")}
                                        </SoftTypography>
                                      </td>
                                      <td>
                                        <SoftTypography variant="caption" color="dark">
                                          {formatStockQuantity(movimiento.stockPrevio, detalle?.tipoUnidad)}
                                        </SoftTypography>
                                      </td>
                                      <td>
                                        <SoftTypography variant="caption" color="dark" fontWeight="medium">
                                          {movimiento.descripcion}
                                        </SoftTypography>
                                      </td>
                                      <td>
                                        <SoftTypography
                                          variant="caption"
                                          fontWeight="bold"
                                          color={getCantidadTone(movimiento.cantidad)}
                                        >
                                          {formatSignedStock(movimiento.cantidad, detalle?.tipoUnidad)}
                                        </SoftTypography>
                                      </td>
                                      <td>
                                        <SoftTypography variant="caption" color="dark" fontWeight="medium">
                                          {formatStockQuantity(movimiento.stockAcumulado, detalle?.tipoUnidad)}
                                        </SoftTypography>
                                      </td>
                                      <td>
                                        <Tooltip title="Ver detalle" placement="left">
                                          <SoftButton
                                            color="secondary"
                                            variant="text"
                                            iconOnly
                                            onClick={(event) => {
                                              event.stopPropagation();
                                              setSelectedMovimientoHistorial(movimiento);
                                            }}
                                          >
                                            <Icon>east</Icon>
                                          </SoftButton>
                                        </Tooltip>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </MovementTable>
                            </MovementTableContainer>
                          </SoftBox>

                          <CustomPagination
                            loading={loading}
                            length={movimientosHistorial?.items?.length || 0}
                            total={movimientosHistorial?.pagination?.total || 0}
                            pages={movimientosHistorial?.pagination?.pages || 0}
                            page={movimientosPage}
                            setPage={setMovimientosPage}
                            noCard
                          />
                        </Card>
                      </MovementPanel>

                      <MovementPanel active={!!selectedMovimientoHistorial} hiddenDirection="right">
                        <SectionCard
                          title="Detalle del movimiento"
                          subtitle={
                            selectedMovimientoHistorial?.detalle?.titulo ||
                            "Selecciona una fila para ver su detalle"
                          }
                          action={
                            selectedMovimientoHistorial ? (
                              <SoftButton
                                variant="text"
                                color="secondary"
                                onClick={() => setSelectedMovimientoHistorial(null)}
                              >
                                <Icon>west</Icon>&nbsp;Volver
                              </SoftButton>
                            ) : null
                          }
                        >
                          {selectedMovimientoHistorial ? (
                            <SoftBox display="flex" flexDirection="column" gap={1.25}>
                              <InfoRow
                                label="Fecha"
                                value={formatDetailDateValue(selectedMovimientoHistorial.fecha)}
                              />
                              <InfoRow
                                label="Stock inicial"
                                value={formatStockQuantity(
                                  selectedMovimientoHistorial.stockPrevio,
                                  detalle?.tipoUnidad
                                )}
                              />
                              <InfoRow
                                label="Movimiento"
                                value={selectedMovimientoHistorial.descripcion || "-"}
                              />
                              {selectedMovimientoEsRemitoProveedor ? (
                                <InfoRow
                                  label="Orden de provision"
                                  value={selectedMovimientoProvision || "-"}
                                  tone="info"
                                />
                              ) : null}
                              <InfoRow
                                label="Usuario"
                                value={selectedMovimientoHistorial.usuario || "Sistema"}
                              />
                              <InfoRow
                                label="Cantidad"
                                value={formatSignedStock(
                                  selectedMovimientoHistorial.cantidad,
                                  detalle?.tipoUnidad
                                )}
                                tone={getCantidadTone(selectedMovimientoHistorial.cantidad)}
                              />
                              <InfoRow
                                label="Stock resultante"
                                value={formatStockQuantity(
                                  selectedMovimientoHistorial.stockAcumulado,
                                  detalle?.tipoUnidad
                                )}
                              />

                              {selectedMovimientoHistorial?.detalle?.subtitulo &&
                                !selectedMovimientoEsVenta ? (
                                <>
                                  <Divider sx={{ my: 0.5 }} />
                                  <SoftBox>
                                    <SoftTypography
                                      variant="caption"
                                      color="text"
                                      fontWeight="bold"
                                      display="block"
                                      mb={0.5}
                                    >
                                      Descripcion completa
                                    </SoftTypography>
                                    <SoftTypography variant="button">
                                      {selectedMovimientoHistorial.detalle.subtitulo}
                                    </SoftTypography>
                                  </SoftBox>
                                </>
                              ) : null}

                              {selectedMovimientoDetailRows.length ? (
                                <>
                                  <Divider sx={{ my: 0.5 }} />
                                  <SoftBox display="flex" flexDirection="column" gap={1}>
                                    {selectedMovimientoDetailRows.map((row, index) => (
                                      <InfoRow
                                        key={`${row.label}-${index}`}
                                        label={row.label}
                                        value={
                                          String(row.label || "").toLowerCase().includes("fecha")
                                            ? formatDetailDateValue(row.value)
                                            : row.value
                                        }
                                      />
                                    ))}
                                  </SoftBox>
                                </>
                              ) : null}
                            </SoftBox>
                          ) : (
                            <SinDatos />
                          )}
                        </SectionCard>
                      </MovementPanel>
                    </MovementStage>
                  )}
                </SoftBox>
              )}

              {tabValue === 2 && actionButtons.length > 0 && (
                <SectionCard title="Acciones disponibles" subtitle="Acciones disponibles para este item">
                  <SoftBox display="flex" flexDirection="column" gap={1.5}>
                    {actionButtons.map((actionButton) => (
                      <ActionButton
                        key={actionButton.key}
                        icon={actionButton.icon}
                        color={actionButton.color}
                        label={actionButton.label}
                        onClick={actionButton.onClick}
                        disabled={actionButton.disabled}
                      />
                    ))}
                  </SoftBox>
                </SectionCard>
              )}
            </>
          )}
        </SoftBox>
      </SoftBox>
    </DetailDrawer>
  );
}

ActionButton.propTypes = {
  icon: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

InfoRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  tone: PropTypes.string,
};

SectionCard.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  children: PropTypes.node,
  action: PropTypes.node,
};

SummaryStatCard.propTypes = {
  icon: PropTypes.string.isRequired,
  iconColor: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  hint: PropTypes.string.isRequired,
  hintColor: PropTypes.string,
};

DrawerProductoDetalle.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  item: PropTypes.object,
  idSucursal: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  idSede: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onEdit: PropTypes.func,
  onAjuste: PropTypes.func,
  onMerma: PropTypes.func,
  onTransferencia: PropTypes.func,
  onTransferHistory: PropTypes.func,
  onIngresar: PropTypes.func,
  onToggleEnabled: PropTypes.func,
  onPublicar: PropTypes.func,
};
